import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Company Details
const COMPANY_DETAILS = {
  name: 'KAJJA GALLERIES',
  address: 'Lukuli Rd, Kampala',
  phone: '+256 759 689 867',
  email: 'kajjagalleries@gmail.com',
  taxId: 'TAX-123456789'
};

// Helper function to add company header
const addCompanyHeader = (doc) => {
  const pageWidth = doc.internal.pageSize.width;
  
  doc.setFillColor(127, 26, 46);
  doc.rect(0, 0, pageWidth, 45, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_DETAILS.name, pageWidth / 2, 28, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text(COMPANY_DETAILS.address, pageWidth / 2, 38, { align: 'center' });
  doc.text(`${COMPANY_DETAILS.phone} | ${COMPANY_DETAILS.email}`, pageWidth / 2, 42, { align: 'center' });
};

// ========== EXPORT 1: generateInvoice ==========
export const generateInvoice = async (order, client, orderItems, payments) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  addCompanyHeader(doc);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 65);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice Number: ${order.orderNumber || 'N/A'}`, 140, 65);
  doc.text(`Date: ${order.orderDate?.toDate ? new Date(order.orderDate.toDate()).toLocaleDateString() : new Date().toLocaleDateString()}`, 140, 72);
  doc.text(`Status: ${order.status?.toUpperCase() || 'PENDING'}`, 140, 79);
  doc.text(`Due Date: ${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}`, 140, 86);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', 20, 100);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(client?.name || 'N/A', 20, 107);
  if (client?.email) doc.text(`Email: ${client.email}`, 20, 114);
  if (client?.phone) doc.text(`Phone: ${client.phone}`, 20, 121);
  if (client?.address) doc.text(`Address: ${client.address}`, 20, 128);
  
  const tableData = orderItems && orderItems.length > 0 
    ? orderItems.map(item => [
        item.productName || 'N/A',
        item.modelNumber || '-',
        item.quantity?.toString() || '0',
        (item.unitSellingPrice || 0).toLocaleString(),
        (item.totalSellingPrice || 0).toLocaleString()
      ])
    : [['No items', '-', '-', '-', '-']];
  
  autoTable(doc, {
    startY: 140,
    head: [['Product', 'Model', 'Quantity', 'Unit Price (UGX)', 'Total (UGX)']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 35 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 40, halign: 'right' },
      4: { cellWidth: 40, halign: 'right' }
    }
  });
  
  const finalY = doc.lastAutoTable?.finalY || 180;
  const totalPaid = payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
  
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(120, finalY + 5, 70, 50, 5, 5, 'FD');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Summary', 125, finalY + 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Subtotal: ${(order.totalSellingPrice || 0).toLocaleString()} UGX`, 125, finalY + 25);
  doc.text(`Total Paid: ${totalPaid.toLocaleString()} UGX`, 125, finalY + 32);
  doc.setTextColor(127, 26, 46);
  doc.setFont('helvetica', 'bold');
  doc.text(`Balance Due: ${(order.balanceDue || 0).toLocaleString()} UGX`, 125, finalY + 39);
  
  const footerY = finalY + 70;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  doc.text('Payment due within 30 days.', pageWidth / 2, footerY + 6, { align: 'center' });
  doc.text(`Tax ID: ${COMPANY_DETAILS.taxId}`, pageWidth / 2, footerY + 12, { align: 'center' });
  
  doc.save(`Invoice_${order.orderNumber || 'unknown'}.pdf`);
};

// ========== EXPORT 2: generateStatement ==========
export const generateStatement = async (client, orders, payments) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  addCompanyHeader(doc);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT STATEMENT', 20, 65);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Statement Period: All Transactions`, 20, 73);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Client: ${client?.name || 'N/A'}`, 20, 85);
  if (client?.email) doc.text(`Email: ${client.email}`, 20, 92);
  if (client?.phone) doc.text(`Phone: ${client.phone}`, 20, 99);
  doc.text(`Statement Date: ${new Date().toLocaleDateString()}`, 20, 106);
  
  const allTransactions = [];
  let runningBalance = 0;
  
  for (const order of (orders || [])) {
    const orderItems = order.items || [];
    allTransactions.push({
      type: 'invoice',
      date: order.orderDate,
      orderNumber: order.orderNumber,
      amount: order.totalSellingPrice || 0,
      items: orderItems,
      balanceChange: order.totalSellingPrice || 0
    });
  }
  
  for (const payment of (payments || [])) {
    const relatedOrder = (orders || []).find(o => o.id === payment.orderId);
    allTransactions.push({
      type: 'payment',
      date: payment.paymentDate,
      orderNumber: relatedOrder?.orderNumber || 'N/A',
      reference: payment.reference,
      amount: payment.amount || 0,
      method: payment.paymentMethod,
      balanceChange: -(payment.amount || 0)
    });
  }
  
  allTransactions.sort((a, b) => {
    const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
    const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
    return dateA - dateB;
  });
  
  const statementData = [];
  for (const transaction of allTransactions) {
    runningBalance += transaction.balanceChange;
    
    if (transaction.type === 'invoice') {
      statementData.push([
        transaction.date?.toDate ? new Date(transaction.date.toDate()).toLocaleDateString() : new Date().toLocaleDateString(),
        transaction.orderNumber,
        'Invoice',
        transaction.amount.toLocaleString(),
        '-',
        runningBalance.toLocaleString()
      ]);
      
      if (transaction.items && transaction.items.length > 0) {
        for (const item of transaction.items) {
          statementData.push([
            '',
            `  - ${item.productName || 'Product'}`,
            item.modelNumber || '',
            `  Qty: ${item.quantity}`,
            `  @ ${(item.unitSellingPrice || 0).toLocaleString()}`,
            ''
          ]);
        }
      }
    } else if (transaction.type === 'payment') {
      statementData.push([
        transaction.date?.toDate ? new Date(transaction.date.toDate()).toLocaleDateString() : new Date().toLocaleDateString(),
        `Payment - ${transaction.orderNumber}`,
        transaction.method || 'Payment',
        '-',
        transaction.amount.toLocaleString(),
        runningBalance.toLocaleString()
      ]);
      if (transaction.reference) {
        statementData.push(['', `  Reference: ${transaction.reference}`, '', '', '', '']);
      }
    }
  }
  
  if (statementData.length === 0) {
    statementData.push([new Date().toLocaleDateString(), '-', 'No transactions', '-', '-', '0']);
  }
  
  autoTable(doc, {
    startY: 115,
    head: [['Date', 'Description / Product', 'Type', 'Debit (UGX)', 'Credit (UGX)', 'Balance (UGX)']],
    body: statementData,
    theme: 'striped',
    headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 55 },
      2: { cellWidth: 25 },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 30, halign: 'right' }
    }
  });
  
  const finalY = doc.lastAutoTable?.finalY || 200;
  const totalOrders = (orders || []).reduce((sum, o) => sum + (o.totalSellingPrice || 0), 0);
  const totalPayments = (payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  const balance = totalOrders - totalPayments;
  
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(120, finalY + 10, 70, 45, 5, 5, 'FD');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 125, finalY + 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Total Invoiced: ${totalOrders.toLocaleString()} UGX`, 125, finalY + 30);
  doc.text(`Total Paid: ${totalPayments.toLocaleString()} UGX`, 125, finalY + 37);
  doc.setTextColor(127, 26, 46);
  doc.setFont('helvetica', 'bold');
  doc.text(`Balance Due: ${balance.toLocaleString()} UGX`, 125, finalY + 44);
  
  const footerY = finalY + 70;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('This statement is computer generated and does not require a signature.', pageWidth / 2, footerY, { align: 'center' });
  doc.text(`For questions, contact ${COMPANY_DETAILS.email} or call ${COMPANY_DETAILS.phone}`, pageWidth / 2, footerY + 6, { align: 'center' });
  
  doc.save(`Statement_${client?.name || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ========== EXPORT 3: generateReceivablesReport ==========
export const generateReceivablesReport = async (clientsWithBalance, allOrders, allPayments) => {
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.width;
  
  addCompanyHeader(doc);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ACCOUNTS RECEIVABLE REPORT', pageWidth / 2, 55, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 65);
  
  // Fetch order items
  const orderItemsMap = {};
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const itemsSnapshot = await getDocs(collection(db, 'orderItems'));
    itemsSnapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };
      if (!orderItemsMap[item.orderId]) {
        orderItemsMap[item.orderId] = [];
      }
      orderItemsMap[item.orderId].push(item);
    });
  } catch (error) {
    console.error('Error fetching order items:', error);
  }
  
  // Fetch clients
  const clientsMap = {};
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const clientsSnapshot = await getDocs(collection(db, 'clients'));
    clientsSnapshot.docs.forEach(doc => {
      clientsMap[doc.id] = { id: doc.id, ...doc.data() };
    });
  } catch (error) {
    console.error('Error fetching clients:', error);
  }
  
  // Build complete sales data
  const salesData = [];
  for (const order of (allOrders || [])) {
    const items = orderItemsMap[order.id] || [];
    const clientData = clientsMap[order.clientId] || {};
    
    salesData.push({
      ...order,
      items: items,
      clientName: clientData.name || 'Unknown',
      clientPhone: clientData.phone || '',
      clientEmail: clientData.email || ''
    });
  }
  
  // Group by client and calculate
  const clientMap = new Map();
  for (const sale of salesData) {
    const balanceDue = sale.balanceDue || 0;
    if (balanceDue > 0) {
      if (!clientMap.has(sale.clientId)) {
        clientMap.set(sale.clientId, {
          clientId: sale.clientId,
          clientName: sale.clientName,
          clientPhone: sale.clientPhone,
          clientEmail: sale.clientEmail,
          orders: [],
          totalBalance: 0,
          totalOrdered: 0,
          totalPaid: 0
        });
      }
      const clientData = clientMap.get(sale.clientId);
      clientData.orders.push(sale);
      clientData.totalBalance += balanceDue;
      clientData.totalOrdered += sale.totalSellingPrice || 0;
      clientData.totalPaid += (sale.totalSellingPrice || 0) - balanceDue;
    }
  }
  
  const clientsWithOutstanding = Array.from(clientMap.values());
  const totalReceivables = clientsWithOutstanding.reduce((sum, c) => sum + c.totalBalance, 0);
  
  // Summary
  const summaryStartY = 85;
  doc.setFillColor(127, 26, 46);
  doc.roundedRect(20, summaryStartY, pageWidth - 40, 40, 5, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL ACCOUNTS RECEIVABLE', pageWidth / 2, summaryStartY + 15, { align: 'center' });
  doc.setFontSize(18);
  doc.text(`${totalReceivables.toLocaleString()} UGX`, pageWidth / 2, summaryStartY + 32, { align: 'center' });
  
  let currentY = summaryStartY + 55;
  
  // Process each client
  for (const client of clientsWithOutstanding) {
    if (currentY > doc.internal.pageSize.height - 120) {
      doc.addPage();
      currentY = 20;
      addCompanyHeader(doc);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('ACCOUNTS RECEIVABLE REPORT (Continued)', pageWidth / 2, 55, { align: 'center' });
      currentY = 70;
    }
    
    // Client Header
    doc.setFillColor(240, 240, 240);
    doc.rect(20, currentY, pageWidth - 40, 30, 'F');
    doc.setTextColor(127, 26, 46);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(client.clientName, 25, currentY + 10);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Phone: ${client.clientPhone || 'N/A'} | Email: ${client.clientEmail || 'N/A'}`, 25, currentY + 20);
    doc.text(`Balance Due: ${client.totalBalance.toLocaleString()} UGX`, pageWidth - 70, currentY + 20);
    
    currentY += 35;
    
    // Orders Table
    const orderData = [];
    for (const order of client.orders) {
      const productList = [];
      for (const item of order.items) {
        productList.push(`${item.productName} (x${item.quantity})`);
      }
      const productsText = productList.join(', ');
      
      orderData.push([
        new Date(order.orderDate?.toDate()).toLocaleDateString(),
        order.orderNumber || 'N/A',
        productsText.substring(0, 50),
        (order.totalSellingPrice || 0).toLocaleString(),
        ((order.totalSellingPrice || 0) - (order.balanceDue || 0)).toLocaleString(),
        (order.balanceDue || 0).toLocaleString(),
        order.status || 'Pending'
      ]);
    }
    
    if (orderData.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [['Date', 'Order #', 'Products', 'Total', 'Paid', 'Balance', 'Status']],
        body: orderData,
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 7 },
        margin: { left: 20, right: 20 }
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, currentY, pageWidth - 20, currentY);
    currentY += 10;
  }
  
  doc.save(`Accounts_Receivable_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ========== EXPORT 4: generatePayablesReport ==========
export const generatePayablesReport = async (accountsPayable) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  addCompanyHeader(doc);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ACCOUNTS PAYABLE REPORT', pageWidth / 2, 55, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 65);
  
  let payables = accountsPayable || [];
  const payablesList = payables.filter(p => p && p.status !== 'paid' && (p.balanceDue || 0) > 0);
  const totalPayables = payablesList.reduce((sum, p) => sum + (p.balanceDue || 0), 0);
  
  // Summary
  const summaryStartY = 85;
  doc.setFillColor(127, 26, 46);
  doc.roundedRect(20, summaryStartY, pageWidth - 40, 40, 5, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL ACCOUNTS PAYABLE', pageWidth / 2, summaryStartY + 15, { align: 'center' });
  doc.setFontSize(18);
  doc.text(`${totalPayables.toLocaleString()} UGX`, pageWidth / 2, summaryStartY + 32, { align: 'center' });
  
  let currentY = summaryStartY + 55;
  
  if (payablesList.length > 0) {
    const payablesTableData = payablesList.map(payable => {
      let typeLabel = '';
      if (payable.type === 'employee') typeLabel = 'Employee';
      else if (payable.type === 'subcontractor') typeLabel = 'Subcontractor';
      else if (payable.type === 'supplier') typeLabel = 'Supplier';
      else typeLabel = 'Other';
      
      return [
        payable.name || 'Unknown',
        typeLabel,
        payable.description || '-',
        (payable.balanceDue || 0).toLocaleString()
      ];
    });
    
    autoTable(doc, {
      startY: currentY,
      head: [['Payee', 'Type', 'Description', 'Amount Owed (UGX)']],
      body: payablesTableData,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: 20, right: 20 }
    });
  } else {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.text('No outstanding payables', 20, currentY);
  }
  
  doc.save(`Accounts_Payable_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ========== EXPORT 5: generateClientDemandReport ==========
export const generateClientDemandReport = async (clientsWithBalance, allOrders, allPayments) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  addCompanyHeader(doc);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT DEMAND REPORT', pageWidth / 2, 55, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 65);
  doc.text('Clients with Outstanding Balances - Payment Follow-up Required', 20, 72);
  
  // Fetch order items for product details
  const orderItemsMap = {};
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const itemsSnapshot = await getDocs(collection(db, 'orderItems'));
    itemsSnapshot.docs.forEach(doc => {
      const item = { id: doc.id, ...doc.data() };
      if (!orderItemsMap[item.orderId]) {
        orderItemsMap[item.orderId] = [];
      }
      orderItemsMap[item.orderId].push(item);
    });
  } catch (error) {
    console.error('Error fetching order items:', error);
  }
  
  // Fetch clients
  const clientsMap = {};
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const clientsSnapshot = await getDocs(collection(db, 'clients'));
    clientsSnapshot.docs.forEach(doc => {
      clientsMap[doc.id] = { id: doc.id, ...doc.data() };
    });
  } catch (error) {
    console.error('Error fetching clients:', error);
  }
  
  const demandingClients = (clientsWithBalance || []).filter(c => c && c.balance > 0).sort((a, b) => (b.balance || 0) - (a.balance || 0));
  const totalDemand = demandingClients.reduce((sum, c) => sum + (c.balance || 0), 0);
  
  const summaryStartY = 85;
  doc.setFillColor(127, 26, 46);
  doc.roundedRect(20, summaryStartY, pageWidth - 40, 40, 5, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL OUTSTANDING', pageWidth / 2, summaryStartY + 15, { align: 'center' });
  doc.setFontSize(18);
  doc.text(`${totalDemand.toLocaleString()} UGX`, pageWidth / 2, summaryStartY + 32, { align: 'center' });
  
  let currentY = summaryStartY + 55;
  
  const tableData = demandingClients.map(client => {
    const productSummary = [];
    const clientOrders = (allOrders || []).filter(o => o && o.clientId === client.id);
    for (const order of clientOrders) {
      const items = orderItemsMap[order.id] || [];
      for (const item of items) {
        productSummary.push(`${item.productName} x${item.quantity}`);
      }
    }
    const productList = productSummary.join(', ').substring(0, 50);
    const clientDetail = clientsMap[client.id] || {};
    
    return [
      client.name || clientDetail.name || 'N/A',
      client.phone || clientDetail.phone || 'N/A',
      client.email || clientDetail.email || 'N/A',
      (client.balance || 0).toLocaleString(),
      productList || 'No products'
    ];
  });
  
  autoTable(doc, {
    startY: currentY,
    head: [['Client', 'Phone', 'Email', 'Balance Due', 'Products']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 2 },
    margin: { left: 20, right: 20 }
  });
  
  doc.save(`Client_Demand_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ========== EXPORT 6: generateFinancialPositionReport ==========
// ========== EXPORT 6: generateFinancialPositionReport ==========
export const generateFinancialPositionReport = async (clientsWithBalance, allOrders, allPayments, clients, accountsPayable) => {
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.width;
  const today = new Date();
  
  // Company Header
  doc.setFillColor(127, 26, 46);
  doc.rect(0, 0, pageWidth, 45, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('KAJJA GALLERIES', pageWidth / 2, 28, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text('Lukuli Rd, Kampala | +256 759 689 867 | kajjagalleries@gmail.com', pageWidth / 2, 40, { align: 'center' });
  
  // Title
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('FINANCIAL POSITION REPORT', pageWidth / 2, 60, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${today.toLocaleString()}`, 20, 70);
  
  // ========== USE PASSED DATA, DON'T REFETCH ==========
  // Build orderItemsMap from passed data
  const orderItemsMap = {};
  
  // Get order items from passed allOrders data
  for (const order of (allOrders || [])) {
    if (order.items && Array.isArray(order.items)) {
      orderItemsMap[order.id] = order.items;
    }
  }
  
  // ========== BUILD CLIENTS MAP FROM PASSED DATA ==========
  const clientsMap = {};
  for (const client of (clients || [])) {
    clientsMap[client.id] = client;
  }
  
  // ========== SECTION 1: ACCOUNTS RECEIVABLE ==========
  let currentY = 85;
  
  // Section Header
  doc.setFillColor(245, 158, 11);
  doc.rect(20, currentY, pageWidth - 40, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('SECTION 1: ACCOUNTS RECEIVABLE', 25, currentY + 8);
  
  currentY += 18;
  
  // Build Receivables Data - Only UNPAID items (balance > 0)
  const receivableRows = [];
  let totalReceivables = 0;
  
  for (const order of (allOrders || [])) {
    const balanceDue = order.balanceDue || 0;
    if (balanceDue > 0) {
      const items = orderItemsMap[order.id] || (order.items || []);
      const clientData = clientsMap[order.clientId] || {};
      
      // If order has no items, show order summary
      if (items.length === 0) {
        receivableRows.push([
          clientData.name || 'Unknown Client',
          clientData.phone || 'N/A',
          'Order Summary',
          '-',
          (order.totalSellingPrice || 0).toLocaleString(),
          balanceDue.toLocaleString()
        ]);
      } else {
        for (const item of items) {
          receivableRows.push([
            clientData.name || 'Unknown Client',
            clientData.phone || 'N/A',
            item.productName || item.name || 'Unknown Product',
            item.quantity || 1,
            (item.totalSellingPrice || item.price || 0).toLocaleString(),
            balanceDue.toLocaleString()
          ]);
        }
      }
      totalReceivables += balanceDue;
    }
  }
  
  // Remove duplicates for same client/order
  const uniqueReceivableRows = [];
  const seenKeys = new Set();
  for (const row of receivableRows) {
    const key = `${row[0]}-${row[2]}`;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueReceivableRows.push(row);
    }
  }
  
  if (uniqueReceivableRows.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [['Client Name', 'Phone', 'Product/Service', 'Qty', 'Amount (UGX)', 'Balance (UGX)']],
      body: uniqueReceivableRows,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 8 },
      margin: { left: 20, right: 20 }
    });
    currentY = doc.lastAutoTable.finalY + 5;
  } else {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.text('No outstanding receivables', 25, currentY);
    currentY += 15;
  }
  
  // Subtotal for Receivables
  doc.setFillColor(240, 240, 240);
  doc.rect(20, currentY, pageWidth - 40, 10, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`SUBTOTAL ACCOUNTS RECEIVABLE: ${totalReceivables.toLocaleString()} UGX`, pageWidth - 100, currentY + 7);
  
  currentY += 20;
  
  // ========== SECTION 2: ACCOUNTS PAYABLE ==========
  // Section Header
  doc.setFillColor(239, 68, 68);
  doc.rect(20, currentY, pageWidth - 40, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('SECTION 2: ACCOUNTS PAYABLE', 25, currentY + 8);
  
  currentY += 18;
  
  // Build Payables Data - ONLY UNPAID items (status NOT 'paid', balance > 0, NOT template)
  let payables = accountsPayable || [];
  
  // IMPORTANT FIX: Check both 'currentBalance' and 'balanceDue' fields
  const payablesList = payables.filter(p => {
    if (!p) return false;
    const balance = p.currentBalance !== undefined ? p.currentBalance : p.balanceDue;
    return p.status !== 'paid' &&           // EXCLUDE paid items
           balance > 0 &&                    // EXCLUDE zero balance
           !p.isTemplate;                    // EXCLUDE templates
  });
  
  const payableRows = [];
  let totalPayables = 0;
  
  for (const payable of payablesList) {
    let typeLabel = 'Other';
    let typeIcon = '';
    if (payable.type === 'employee') {
      typeLabel = 'Employee';
      typeIcon = '';
    } else if (payable.type === 'subcontractor') {
      typeLabel = 'Subcontractor';
      typeIcon = '';
    } else if (payable.type === 'supplier') {
      typeLabel = 'Supplier';
      typeIcon = '';
    }
    
    const balance = payable.currentBalance !== undefined ? payable.currentBalance : payable.balanceDue;
    
    payableRows.push([
      `${typeIcon} ${payable.name || 'Unknown'}`,
      typeLabel,
      payable.description || payable.service_type || '-',
      balance.toLocaleString()
    ]);
    totalPayables += balance;
  }
  
  if (payableRows.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [['Payee Name', 'Type', 'Description', 'Amount Owed (UGX)']],
      body: payableRows,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: 20, right: 20 }
    });
    currentY = doc.lastAutoTable.finalY + 5;
  } else {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.text('No outstanding payables', 25, currentY);
    currentY += 15;
  }
  
  // Subtotal for Payables
  doc.setFillColor(240, 240, 240);
  doc.rect(20, currentY, pageWidth - 40, 10, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`SUBTOTAL ACCOUNTS PAYABLE: ${totalPayables.toLocaleString()} UGX`, pageWidth - 100, currentY + 7);
  
  currentY += 20;
  
  // ========== SECTION 3: SUMMARY TOTALS ==========
  // Calculate Total Sales
  const totalSales = (allOrders || []).reduce((sum, order) => sum + (order.totalSellingPrice || 0), 0);
  
  // Calculate Cash in Hand from passed payments data
  let totalInflow = 0;
  let totalOutflow = 0;
  
  // Use passed allPayments data instead of refetching
  if (allPayments && Array.isArray(allPayments)) {
    totalInflow = allPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  }
  
  // Calculate outflow from accountsPayable payment history
  for (const payable of (accountsPayable || [])) {
    if (payable.paymentHistory && payable.paymentHistory.length > 0) {
      totalOutflow += payable.paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0);
    }
  }
  
  const cashInHand = totalInflow - totalOutflow;
  
  // Net Position
  const netPosition = cashInHand + totalReceivables - totalPayables;
  
  // Financial Health Assessment
  const totalLiquidAssets = cashInHand + totalReceivables;
  const isHealthy = totalLiquidAssets >= totalPayables;
  const healthStatus = isHealthy ? 'GOOD' : 'BAD';
  const healthColor = isHealthy ? [16, 185, 129] : [239, 68, 68];
  const healthMessage = isHealthy 
    ? 'GOOD: You have enough assets to cover your payables' 
    : 'BAD: Your payables exceed your available assets!';
  
  // Section Header
  doc.setFillColor(127, 26, 46);
  doc.rect(20, currentY, pageWidth - 40, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('SECTION 3: SUMMARY TOTALS', 25, currentY + 8);
  
  currentY += 18;
  
  // Summary Table
  const summaryRows = [
    ['Total Sales', `${totalSales.toLocaleString()} UGX`],
    ['Cash in Hand', `${cashInHand.toLocaleString()} UGX`],
    [' Accounts Receivable (Money owed to you)', `${totalReceivables.toLocaleString()} UGX`],
    [' Total Liquid Assets (Cash + Receivables)', `${totalLiquidAssets.toLocaleString()} UGX`],
    ['Accounts Payable (Money you owe)', `${totalPayables.toLocaleString()} UGX`],
    ['Net Position', `${netPosition >= 0 ? 'POSITIVE' : 'NEGATIVE'}: ${Math.abs(netPosition).toLocaleString()} UGX`]
  ];
  
  autoTable(doc, {
    startY: currentY,
    head: [['Metric', 'Amount']],
    body: summaryRows,
    theme: 'striped',
    headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 9 },
    margin: { left: 20, right: 20 }
  });
  
  currentY = doc.lastAutoTable.finalY + 10;
  
  // ========== FINANCIAL HEALTH INDICATOR ==========
  doc.setFillColor(healthColor[0], healthColor[1], healthColor[2]);
  doc.roundedRect(20, currentY, pageWidth - 40, 18, 5, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`FINANCIAL HEALTH: ${healthMessage}`, pageWidth / 2, currentY + 12, { align: 'center' });
  
  // ========== FOOTER ==========
  const footerY = currentY + 30;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Accounts Receivable = Money customers owe you | Accounts Payable = Money you owe to others', pageWidth / 2, footerY, { align: 'center' });
  doc.text('Note: Only unpaid payables are shown in this report.', pageWidth / 2, footerY + 6, { align: 'center' });
  doc.text(`Report ID: FIN-${today.getFullYear()}${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}-${Date.now()}`, pageWidth / 2, footerY + 12, { align: 'center' });
  
  doc.save(`Financial_Position_Report_${today.toISOString().split('T')[0]}.pdf`);
};